import { inject, Injectable } from '@angular/core';
import { GlobalStateService } from '@base/services/private/global-state.service';

export type AgendaFavoriteGuidanceContext = 'AGENDA_SESSION_FAVORITE' | 'AGENDA_ON_DEMAND_FAVORITE';

export type PlannerGuidanceContext =
  AgendaFavoriteGuidanceContext | 'COMPANY_BOOKMARK' | 'PEOPLE_BOOKMARK' | 'PRODUCT_BOOKMARK' | 'REQUEST' | 'ACCEPT';

const STORAGE_KEY_PREFIX = 'planner-guidance:v1';

@Injectable({ providedIn: 'root' })
export class PlannerGuidanceService {
  private readonly globalStateSrv = inject(GlobalStateService);

  private readonly shownCache = new Map<string, Set<string>>();

  private getUserScope(): string {
    const state = this.globalStateSrv.getState();
    return String(state?.validate?.hashed_id ?? 'guest');
  }

  private getEventHash(): string {
    return String(this.globalStateSrv.getState()?.loader?.event_hash ?? 'unknown');
  }

  private getStorageKey(): string {
    return `${STORAGE_KEY_PREFIX}:${this.getEventHash()}:${this.getUserScope()}`;
  }

  private loadShown(): Set<string> {
    const storageKey = this.getStorageKey();
    const cached = this.shownCache.get(storageKey);
    if (cached) return cached;

    try {
      const raw = localStorage.getItem(storageKey);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      const shown = new Set<string>(
        Array.isArray(parsed) ? parsed.filter((value: unknown) => typeof value === 'string') : []
      );
      this.shownCache.set(storageKey, shown);
      return shown;
    } catch {
      const shown = new Set<string>();
      this.shownCache.set(storageKey, shown);
      return shown;
    }
  }

  private persist(shown: Set<string>): void {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify([...shown]));
    } catch {
      // Storage failure must not block user action
    }
  }

  /** Read eligibility without consuming the one-time guidance. */
  canShow(context: PlannerGuidanceContext): boolean {
    return !this.globalStateSrv.isAnon() && !this.loadShown().has(context);
  }

  /** Check if a context has already been shown and mark it as shown. Returns true on first call. */
  checkAndMark(context: PlannerGuidanceContext): boolean {
    if (this.globalStateSrv.isAnon()) return false;
    const shown = this.loadShown();
    if (shown.has(context)) return false;

    shown.add(context);
    this.persist(shown);
    return true;
  }

  /** Agenda items have null time for on-demand content and non-null time for scheduled sessions. */
  checkAndMarkAgendaFavorite(time: string | null): AgendaFavoriteGuidanceContext | null {
    const context: AgendaFavoriteGuidanceContext =
      time === null ? 'AGENDA_ON_DEMAND_FAVORITE' : 'AGENDA_SESSION_FAVORITE';

    return this.checkAndMark(context) ? context : null;
  }

  /** Rollback a context if modal presentation failed */
  rollback(context: PlannerGuidanceContext): void {
    const shown = this.loadShown();
    shown.delete(context);
    this.persist(shown);
  }
}
